// Typed client for /api/bot/* (M25 Daten-Bot). Every function mirrors one
// endpoint documented in functions/intranet/src/functions/bot.js — see the
// M25 P1 handoff (api_shapes.md) for the verbatim contract. Types below
// mirror that contract 1:1; BIGINT columns already arrive Number()-normalised
// from the backend, so ids are plain `number` here too.

import { api } from '../../../shared/api'

// ── Shared types ─────────────────────────────────────────────────────────────

export interface BotModelPrice {
  inPerMtok: number
  outPerMtok: number
  cachedInPerMtok: number | null
  cacheWritePerMtok: number | null
  currency: string
  priceStatus: 'verified' | 'estimate' | 'unknown'
}

export interface BotModel {
  id: number
  provider: 'anthropic' | 'openai' | 'google'
  modelId: string
  label: string
  family: string
  supportsReasoning: boolean
  contextTokens: number | null
  maxOutputTokens: number | null
  configured: boolean
  price: BotModelPrice | null
}

/** GET /api/bot/models/catalog row — same shape as BotModel plus is_enabled
 *  (IT.Admin only; the plain BotModel list above is enabled-only). */
export type BotCatalogModel = BotModel & { isEnabled: boolean }

/** POST /api/bot/models/sync response (IT.Admin only). `checked` is the
 *  fetched-model count per provider, or null when that provider has no key
 *  configured or its fetch failed (see `errors`). `errors` never carries key
 *  material — just which provider failed and a short message. */
export interface BotModelSyncResult {
  added: { provider: string; modelId: string; label: string }[]
  vanished: { provider: string; modelId: string }[]
  checked: Record<string, number | null>
  errors: Record<string, string>
}

export interface BotConversation {
  id: number
  /** Present ONLY when the caller is IT.Admin (conditional key, per backend). */
  userUpn?: string
  title: string | null
  updatedAt: string
  createdAt: string
  totalCostMicroEur: number
  defaultModels: string[]
}

export interface BotToolCall {
  seq: number
  tool: string
  sqlText: string | null
  rowCount: number | null
  durationMs: number | null
  error: string | null
}

export interface BotUsage {
  tokensIn: number
  tokensOut: number
  tokensCachedIn: number
  tokensCacheWrite: number
  tokensReasoning: number
  costMicroEur: number | null
  costStatus: 'computed' | 'unknown'
}

export interface BotAnswer {
  answerId: number
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled'
  content: string | null
  error: string | null
  modelId: string
  modelLabel: string
  /** bot.Model.provider — drives AnswerCard's dot + card tint (botColors.ts). */
  provider: 'anthropic' | 'openai' | 'google'
  startedAt: string | null
  finishedAt: string | null
  durationMs: number | null
  toolRounds: number
  usage: BotUsage | null
  toolCalls: BotToolCall[]
}

/** GET /api/bot/turns/{id} — also the shape `useBotTurn`'s poll target returns. */
export interface BotTurn {
  turnId: number
  conversationId: number
  question: string
  askedAt: string
  answers: BotAnswer[]
}

/**
 * The turn shape nested inside GET /api/bot/conversations/{id} (`turns[]`).
 * On the wire this omits `conversationId` (it's implied by the parent
 * object) but carries `ordinal`/`askedByUpn`/`modelsRequested` instead —
 * `getConversation()` below stamps the parent's id onto each turn so this
 * type can extend `BotTurn` and both shapes are interchangeable wherever
 * only the common fields (turnId/question/askedAt/answers) are used.
 */
export interface BotConversationTurn extends BotTurn {
  ordinal: number
  askedByUpn: string
  modelsRequested: string[]
}

export interface BotConversationDetail {
  id: number
  title: string | null
  turns: BotConversationTurn[]
}

export interface BotMemoryItem {
  id: number
  kind: 'preference' | 'fact' | 'pinned' | 'style'
  content: string
  sourceTurnId: number | null
  createdAt: string
}

export interface BotSettings {
  defaultModels: string[]
  compareLayout: string | null
  memoryEnabled: boolean
  /** { [provider]: swatchId } — BOT_SWATCHES ids from botColors.ts. Always
   *  an object (never undefined) — {} until migration 264 has run or the
   *  user hasn't picked any colors yet, per bot-settings-get's fallback. */
  providerColors: Record<string, string>
}

/** One text entry of GET/PATCH /api/bot/org-settings. `updatedBy`/`updatedAt`
 *  are `null` for a key that has never been saved (no row yet). */
export interface BotOrgSettingEntry {
  text: string
  updatedBy: string | null
  updatedAt: string | null
}

/** GET/PATCH /api/bot/org-settings — IT.Admin-editable soft-guidance layer
 *  (migration 268's bot.OrgSetting). Each key is `null` when it has no
 *  saved row yet (feature silently off for that key). `persisted: false`
 *  means migration 268 hasn't run on this DB yet — both keys are always
 *  `null` in that case and PATCH cannot save anything. */
export interface BotOrgSettings {
  guardrails: BotOrgSettingEntry | null
  knowledge: BotOrgSettingEntry | null
  persisted: boolean
}

export interface MonthUsage {
  monthSpendMicroEur: number
  capEur: number
  mine: number
}

export interface BotUsageProviderRow {
  provider: 'anthropic' | 'openai' | 'google'
  answers: number
  tokensIn: number
  tokensOut: number
  costMicroEur: number
  unknownCosts: number
}

/** GET /api/bot/usage/report — per-provider cost breakdown for `month`
 *  ('YYYY-MM', current UTC month). `org` = every bot user's usage (same
 *  visibility as MonthUsage.monthSpendMicroEur); `mine` = the caller's own. */
export interface BotUsageUserRow {
  user: string
  answers: number
  tokensIn: number
  tokensOut: number
  costMicroEur: number
  unknownCosts: number
}

export interface BotUsageReport {
  month: string
  capEur: number
  org: BotUsageProviderRow[]
  mine: BotUsageProviderRow[]
  /** Present ONLY when the caller is IT.Admin (conditional key, per backend). */
  users?: BotUsageUserRow[]
}

// ── Request bodies ───────────────────────────────────────────────────────────

export interface ConversationCreateRequest {
  title?: string
  defaultModels?: string[]
}

export interface ConversationPatchRequest {
  title?: string
  archived?: boolean
}

export interface TurnCreateRequest {
  question: string
  models: string[]
  conversationId?: number
}

export interface AskTurnResponse {
  turnId: number
  conversationId: number
  answerIds: number[]
}

// Narrower than a plain `Partial<BotSettings>`: `compareLayout` on
// `BotSettings` is `string | null` (the GET response shape), but the PATCH
// contract only ever applies this field when it is `typeof 'string'` — a
// `null` silently no-ops server-side rather than clearing the setting. No
// current caller passes `null` (SettingsTab.tsx only ever sends the
// `CompareLayout` union), but this keeps a future caller from type-checking
// a patch the backend would silently ignore.
export type SettingsPatchRequest = Partial<Omit<BotSettings, 'compareLayout'>> & { compareLayout?: string }

/** PATCH /api/bot/org-settings body — each field, when present, replaces
 *  that key's text (empty string clears it back to "no row"/off). Omitting
 *  a field leaves that key untouched. */
export interface OrgSettingsPatchRequest {
  guardrails?: string
  knowledge?: string
}

// ── Endpoints ─────────────────────────────────────────────────────────────────

export function getModels() {
  return api.get<BotModel[]>('/api/bot/models')
}

/** GET /api/bot/models/catalog — IT.Admin only. */
export function getModelCatalog() {
  return api.get<BotCatalogModel[]>('/api/bot/models/catalog')
}

/** POST /api/bot/models/sync — IT.Admin only. */
export function syncModels() {
  return api.post<BotModelSyncResult>('/api/bot/models/sync', {})
}

/** PATCH /api/bot/models/{id} — IT.Admin only. */
export function patchModel(id: number, body: { isEnabled: boolean }) {
  return api.patch<{ id: number; isEnabled: boolean }>(`/api/bot/models/${id}`, body)
}

export function listConversations(opts?: { archived?: boolean }) {
  const path = opts?.archived ? '/api/bot/conversations?archived=1' : '/api/bot/conversations'
  return api.get<BotConversation[]>(path)
}

export function createConversation(body: ConversationCreateRequest) {
  return api.post<{ id: number }>('/api/bot/conversations', body)
}

export function patchConversation(id: number, body: ConversationPatchRequest) {
  return api.patch<{ id: number }>(`/api/bot/conversations/${id}`, body)
}

export async function getConversation(id: number): Promise<BotConversationDetail> {
  // Wire shape omits `conversationId` on each nested turn (see
  // `BotConversationTurn`) — stamp it on client-side from the parent id.
  const raw = await api.get<{
    id: number
    title: string | null
    turns: Array<Omit<BotConversationTurn, 'conversationId'>>
  }>(`/api/bot/conversations/${id}`)
  return { ...raw, turns: raw.turns.map((t) => ({ ...t, conversationId: raw.id })) }
}

export function askTurn(body: TurnCreateRequest) {
  return api.post<AskTurnResponse>('/api/bot/turns', body)
}

export function getTurn(id: number) {
  return api.get<BotTurn>(`/api/bot/turns/${id}`)
}

export function cancelAnswer(id: number) {
  return api.post<{ ok: true }>(`/api/bot/answers/${id}/cancel`, {})
}

export function getMemory() {
  return api.get<BotMemoryItem[]>('/api/bot/memory')
}

export function deleteMemory(id: number) {
  return api.delete<{ ok: true }>(`/api/bot/memory/${id}`)
}

export function getSettings() {
  return api.get<BotSettings>('/api/bot/settings')
}

export function patchSettings(body: SettingsPatchRequest) {
  return api.patch<{ ok: true }>('/api/bot/settings', body)
}

/** GET /api/bot/org-settings — IT.Admin only. */
export function getOrgSettings() {
  return api.get<BotOrgSettings>('/api/bot/org-settings')
}

/** PATCH /api/bot/org-settings — IT.Admin only. */
export function patchOrgSettings(body: OrgSettingsPatchRequest) {
  return api.patch<BotOrgSettings>('/api/bot/org-settings', body)
}

export function getMonthUsage() {
  return api.get<MonthUsage>('/api/bot/usage/month')
}

export function getUsageReport() {
  return api.get<BotUsageReport>('/api/bot/usage/report')
}
